import { useEffect, useState, useRef, useCallback } from "react";
import {
  fetchZonesPOReports,
  fetchZonePartiesPOReports,
  fetchCollectionReceivedReports,
  fetchMonthsDropdown
} from "Service/ApiService";

/* ======================= HELPERS ======================= */
const useDebounce = (value, delay = 600) => {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);

  return debounced;
};

// ================= FY LABEL FOR MONTH WISE HEADER =================
export const getSelectedFYLabel = (filters, yearOptions) => {
  // Agar user ne year select kiya hai
  if (Array.isArray(filters?.year) && filters.year.length > 0) {
    return filters.year[0]?.value || "";
  }

  // Default → saare year options
  return yearOptions?.[0]?.value || "";
};


/* ================= FY HELPERS ================= */
export const getCurrentFYLabel = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = today.getMonth();
  const currentFYStart = month < 3 ? year - 1 : year;

  const currFY = `${currentFYStart}-${String(currentFYStart + 1).slice(-2)}`;
  return currFY;
};

export const getFYMonthFlowHeader = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = today.getMonth();
  const currentFYStart = month < 3 ? year - 1 : year;
  const start = new Date(currentFYStart, 3, 1);
  const end = new Date(year, month, 1);

  const format = (d) =>
    d.toLocaleString("en-GB", {
      month: "short",
      year: "2-digit"
    }).toUpperCase();

  return `${format(start)} TO ${format(end)}`;
};

const getFYYearOptions = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = today.getMonth();
  const currentFYStart = month < 3 ? year - 1 : year;

  return [
    {
      
      label: `${currentFYStart}-${String(currentFYStart + 1).slice(-2)}`,
      value: `${currentFYStart}-${String(currentFYStart + 1).slice(-2)}`
    }
  ];
};

const getCurrentFYRange = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  const fyStartYear = month < 3 ? year - 1 : year;
  return {
    start: new Date(fyStartYear, 3, 1),
    end: new Date(fyStartYear + 1, 2, 31),
    fyLabelShort: `${fyStartYear}-${String(fyStartYear + 1).slice(-2)}`,
    fyLabelLong: `${fyStartYear}-${fyStartYear + 1}`
  };
};

const parseMonthOptionDate = (raw) => {
  const str = String(raw || "").trim();
  if (!str) return null;

  const monthMap = {
    jan: 0, january: 0,
    feb: 1, february: 1,
    mar: 2, march: 2,
    apr: 3, april: 3,
    may: 4,
    jun: 5, june: 5,
    jul: 6, july: 6,
    aug: 7, august: 7,
    sep: 8, sept: 8, september: 8,
    oct: 9, october: 9,
    nov: 10, november: 10,
    dec: 11, december: 11
  };

  const monthYearMatch = str.match(/([A-Za-z]+)\s*[-/ ]\s*(\d{2,4})/);
  if (monthYearMatch) {
    const mm = monthMap[monthYearMatch[1].toLowerCase()];
    let yy = Number(monthYearMatch[2]);
    if (yy < 100) yy += 2000;
    if (mm !== undefined && Number.isFinite(yy)) return new Date(yy, mm, 1);
  }

  const yearMonthMatch = str.match(/(\d{4})\s*[-/ ]\s*(\d{1,2})/);
  if (yearMonthMatch) {
    const yy = Number(yearMonthMatch[1]);
    const mm = Number(yearMonthMatch[2]) - 1;
    if (Number.isFinite(yy) && mm >= 0 && mm <= 11) return new Date(yy, mm, 1);
  }

  return null;
};

const isMonthOptionInCurrentFY = (monthObj) => {
  const { start, end, fyLabelShort, fyLabelLong } = getCurrentFYRange();
  const fyText = String(monthObj?.fy || monthObj?.year || "").trim().toLowerCase();
  if (fyText) {
    const shortNorm = fyLabelShort.toLowerCase();
    const longNorm = fyLabelLong.toLowerCase();
    if (fyText.includes(shortNorm) || fyText.includes(longNorm)) return true;
    return false;
  }

  const parsedDate =
    parseMonthOptionDate(monthObj?.value) || parseMonthOptionDate(monthObj?.label);

  // If backend sends only month-name (no year), keep it to avoid empty dropdown.
  if (!parsedDate) return true;

  return parsedDate >= start && parsedDate <= end;
};

/* ================= CONTROLLER ================= */
const CollectionReceivedController = () => {
  const user = JSON.parse(localStorage.getItem("user"));
  const formatCustomerLabel = (p) =>
    `${p?.partyName?.trim() || ""} - ${p?.city?.trim() || ""} - ${p?.partyCode?.trim() || ""}`;

  /* ================= STATE ================= */
  const [tableRows, setTableRows] = useState([]);
  const [months, setMonths] = useState([]);
  const [grandTotal, setGrandTotal] = useState({});
  const [summaryChart, setSummaryChart] = useState([]);
  const [barChartData, setBarChartData] = useState([]);

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);

  const [zoneOptions, setZoneOptions] = useState([]);
  const [customerNameOptions, setCustomerNameOptions] = useState([]);
  const [monthOptions, setMonthOptions] = useState([]);
  const [yearOptions, setYearOptions] = useState([]);

  const [todaysDate, setTodaysDate] = useState("");
  const [fyLabel, setFyLabel] = useState("");
  const [zoneTotals, setZoneTotals] = useState({});

  const [filters, setFilters] = useState({
    zone: [],
    customerName: [],
    fromDate: "",
    toDate: "",
    year: getFYYearOptions(),
    month: []
  });

  const debouncedFilters = useDebounce(filters, 600);

  /* ================= LOADING ================= */
  const [loadingMain, setLoadingMain] = useState(true);
  // Start as "loading" so dropdownsLoaded can't be true before fetch effects run.
  const [loadingZones, setLoadingZones] = useState(true);
  const [loadingCustomers, setLoadingCustomers] = useState(true);
  const [loadingMonths, setLoadingMonths] = useState(true);
  const [initialReportResolved, setInitialReportResolved] = useState(false);
  const isFromApp =  localStorage.getItem('isFromApp') === 'true';
  const cloading =
    !initialReportResolved ||
    loadingMain || loadingZones || loadingCustomers || loadingMonths;

  const dropdownsLoaded =
    !loadingZones && !loadingCustomers && !loadingMonths;

  /* 🔐 FIRST CALL LOCK */
  const firstApiCallRef = useRef(false);
  const filtersRef = useRef(filters);
  const lastRequestedRef = useRef(null);
  const initialFiltersDoneRef = useRef(false);

  /* ================= HANDLERS ================= */
  const handleChangePage = (_, newPage) => setPage(newPage + 1);

  const handleFilterChange = (field) => (value) => {
    setFilters((p) => ({ ...p, [field]: value || [] }));
    setPage(1);
  };

  const handleDateFromChange = (date) => {
    setFilters((p) => ({ ...p, fromDate: date }));
    setPage(1);
  };

  const handleDateToChange = (date) => {
    setFilters((p) => ({ ...p, toDate: date }));
    setPage(1);
  };

  /* ================= LOAD ZONES ================= */
  useEffect(() => {
    const loadZones = async () => {
      try {
        setLoadingZones(true);
        const res = await fetchZonesPOReports(user.userCode, "collection");
        if (res?.status) {
          setZoneOptions(
            res.zones.map((z, idx) => ({
              label: z.region?.trim() || "(NO REGION)",
              value: `${z.zoneCode}__${idx}`,
              zoneCode: z.zoneCode
            }))
          );
        }
      } finally {
        setLoadingZones(false);
      }
    };
    loadZones();
  }, [user.userCode]);

  const loadAllCustomers = useCallback(async () => {
    try {
      setLoadingCustomers(true);
      const res = await fetchZonePartiesPOReports(user.userCode, {
        type: "collection"
      });
      if (res?.status) {
        setCustomerNameOptions(
          res.parties.map((p) => ({
            label: formatCustomerLabel(p),
            value: p.partyCode
          }))
        );
      } else {
        setCustomerNameOptions([]);
      }
    } finally {
      setLoadingCustomers(false);
    }
  }, [user.userCode]);

  /* ================= LOAD CUSTOMERS ================= */
  useEffect(() => {
    loadAllCustomers();
  }, [loadAllCustomers]);

  /* ================= LOAD MONTHS ================= */
  useEffect(() => {
    const loadMonths = async () => {
      try {
        setLoadingMonths(true);
        const res = await fetchMonthsDropdown(user.userCode, "collection");
        if (res?.status) {
          const rawMonths = Array.isArray(res.months) ? res.months : [];
          //const currentFYMonths = rawMonths.filter(isMonthOptionInCurrentFY);
         // const finalMonths = currentFYMonths.length ? currentFYMonths : rawMonths;

          setMonthOptions(
            rawMonths.map((m) => ({
              label: m.label,
              value: m.value
            }))
          );
        }
      } finally {
        setLoadingMonths(false);
      }
    };
    loadMonths();
  }, [user.userCode]);

  /* ================= AUTO SELECT ALL ================= */
  useEffect(() => {
    setYearOptions(getFYYearOptions());
  }, []);

  useEffect(() => {
    // One-time initialization AFTER dropdowns are loaded: set defaults in one state update.
    if (!dropdownsLoaded || initialFiltersDoneRef.current) return;
    initialFiltersDoneRef.current = true;

    setFilters((prev) => {
      const next = {
        ...prev,
        zone: prev.zone.length ? prev.zone : zoneOptions,
        customerName: prev.customerName.length ? prev.customerName : customerNameOptions,
        month: prev.month.length ? prev.month : monthOptions
        // year already initialized in initial state
      };
      filtersRef.current = next;
      return next;
    });
  }, [dropdownsLoaded, zoneOptions, customerNameOptions, monthOptions]);

  /* ================= MAIN API ================= */
  const getFiltersSignature = useCallback((f) => {
    const zoneCodes = [...new Set((f?.zone ?? []).map(z => z.zoneCode))].join(",");
    const partyCodes = (f?.customerName ?? []).map(c => c.value).join(",");
    const monthsStr = (f?.month ?? []).map(m => m.value).join(",");
    const years = (f?.year ?? []).map(y => y.value).join(",");
    return `${zoneCodes}|${partyCodes}|${monthsStr}|${years}|${f?.fromDate || ""}|${f?.toDate || ""}`;
  }, []);

  const getCollectionReceived = useCallback(async () => {
    const f = filtersRef.current;
    try {
      setLoadingMain(true);

      const zoneCodes = [...new Set((f?.zone ?? []).map(z => z.zoneCode))].join(",");
      const partyCodes = (f?.customerName ?? []).map(c => c.value).join(",");
      const monthsStr = (f?.month ?? []).map(m => m.value).join(",");
      const years = (f?.year ?? []).map(y => y.value).join(",");

      const res = await fetchCollectionReceivedReports(
        user.userCode,
        page,
        zoneCodes,
        partyCodes,
        f?.fromDate || "",
        f?.toDate || "",
        years,
        monthsStr
      );

      setTodaysDate(res?.as_on_date || "");
      setFyLabel(res?.fyLabel || "");

      if (Array.isArray(res?.partyTable)) {
        const rows = [];
        res.partyTable.forEach(z =>
          z.customers?.forEach(c =>
            rows.push({
              zone: z.zone,
              customer: c.partyName,
              values: c.months || {},
              total: Number(c.total || 0)
            })
          )
        );
        setTableRows(rows);
        setTotalPages(res.pagination?.totalPages || 0);
        setTotalRecords(res.pagination?.totalRecords || 0);
      } else {
        setTableRows([]);
        setTotalPages(0);
        setTotalRecords(0);
      }

      setSummaryChart(res.monthChart || []);
      setMonths(res.monthChart?.map(m => m.month) || []);

      if (Array.isArray(res?.top10)) {
        setBarChartData(
          res.top10.map(t =>
            t.FY_AMOUNT !== undefined
              ? { customerName: t.customerName, FY_SELECTED: t.FY_AMOUNT }
              : {
                  customerName: t.customerName,
                  FY_CURRENT: t.FY_CURRENT,
                  FY_PREVIOUS: t.FY_PREVIOUS
                }
          )
        );
      } else {
        setBarChartData([]);
      }
    } finally {
      setInitialReportResolved(true);
      setLoadingMain(false);
    }
  }, [page, user.userCode]);

  /* ================= SINGLE FINAL API EFFECT ================= */
  useEffect(() => {
    if (!dropdownsLoaded) return;

    if (!firstApiCallRef.current) {
      firstApiCallRef.current = true;
      // First call: use whatever init set into filtersRef (don’t wait for debounce)
      lastRequestedRef.current = getFiltersSignature(filtersRef.current);
      getCollectionReceived();
      return;
    }

    // Subsequent calls: only when debouncedFilters actually changes (or page changes via dep above)
    const sig = getFiltersSignature(debouncedFilters);
    if (lastRequestedRef.current === sig) return;
    lastRequestedRef.current = sig;
    filtersRef.current = debouncedFilters;
    getCollectionReceived();
  }, [dropdownsLoaded, page, debouncedFilters, getCollectionReceived, getFiltersSignature]);

  /* ================= GRAND TOTAL ================= */
  useEffect(() => {
    if (!tableRows.length || !months.length) {
      setGrandTotal({});
      return;
    }

    const gt = {};
    months.forEach(m => {
      gt[m] = tableRows.reduce((s, r) => s + (r.values[m] || 0), 0);
    });
    gt.total = tableRows.reduce((s, r) => s + (r.total || 0), 0);
    setGrandTotal(gt);
  }, [tableRows, months]);

  /* ================= ZONE TOTAL ================= */
  useEffect(() => {
    const agg = {};
    tableRows.forEach(r => {
      if (!agg[r.zone]) {
        agg[r.zone] = { months: {}, total: 0 };
        months.forEach(m => (agg[r.zone].months[m] = 0));
      }
      months.forEach(m => (agg[r.zone].months[m] += r.values[m] || 0));
      agg[r.zone].total += r.total || 0;
    });
    setZoneTotals(agg);
  }, [tableRows, months]);

  /* ================= CUSTOMER SEARCH ================= */
  const handleCustomerSearch = async (inputValue) => {
    try {
      setLoadingCustomers(true);
      const res = await fetchZonePartiesPOReports(user.userCode, {
        search: inputValue,
        type: "outstanding"
      });

      if (res?.status && Array.isArray(res.parties)) {
        setCustomerNameOptions(
          res.parties.map((p) => ({
            label: formatCustomerLabel(p),
            value: p.partyCode
          }))
        );
      }
    } catch (err) {
      console.error("Customer search error:", err);
    } finally {
      setLoadingCustomers(false);
    }
  };
  

  return {
    summaryChart,
    barChartData,
    cloading,
    page,
    totalPages,
    totalRecords,
    zoneOptions,
    customerNameOptions,
    yearOptions,
    monthOptions,
    filters,
    handleFilterChange,
    handleDateFromChange,
    handleDateToChange,
    handleChangePage,
    months,
    tableRows,
    grandTotal,
    todaysDate,
    prevAndCurrentFY: getCurrentFYLabel(),
    getFYMonthFlowHeader,
    getSelectedFYLabel,
    fyLabel,
    zoneTotals,
    handleCustomerSearch,
    loadAllCustomers,
    isFromApp
  };
};

export default CollectionReceivedController;

